<template>
  <div class="base-accordion">
    <slot />
  </div>
</template>

<script>

/**
 * Vue: Base accordion (base-accordion)
 * -----------------------------------------------------------------------------
 * An accordion component.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean} collapsible - Allow all accordion items to be closed. If
 * false, then at least one item must be open at all times.
 * @param {Number|Boolean} defaultIndex - Which accordion item should be open
 * initially.
 * @param {Boolean} multiple - Allow multiple accordions to be opened at once.
 */
export default {
  name: 'BaseAccordion',

  /**
   * Provide functionality down to child components.
   * - Used by the `base-accordion-item` component.
   */
  provide() {
    return {
      getActive: this.getActive,
      register: this.register,
      unregister: this.unregister,
      setActive: this.setActive,
    }
  },

  props: {
    collapsible: {
      type: Boolean,
      default: true,
    },
    defaultIndex: {
      // eslint-disable-next-line vue/prefer-prop-type-boolean-first
      type: [Number, Boolean],
      default: 0,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'accordions-toggled': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      items: [],
    }
  },

  computed: {

    /**
     * Compute active accordion items.
     * @returns {Array}
     */
    activeItems() {
      return this.items.filter((item) => item.active)
    },

    /**
     * Compute count of active accordion items.
     * @returns {Number}
     */
    activeItemsCount() {
      return this.activeItems.length
    },

    /**
     * Compute if multiple items are active.
     * @returns {Boolean}
     */
    isMultipleActive() {
      return this.activeItemsCount > 1
    },

    /**
     * Compute active states for all accordions.
     * - Joins all active states as a string so that its easier to Vue's watcher
     *   to compare
     * @returns {String}
     */
    itemsActiveStates() {
      return this.items.map((item) => item.active).join('/')
    },
  },

  watch: {

    /**
     * Watch if the accordion active states updates and emit an event.
     */
    itemsActiveStates() {
      this.$emit('accordions-toggled', this.items)
    },
  },

  methods: {

    /**
     * Check if a given accordion item is active.
     * @param {Object} componentItem - Base Accordion Item component instance
     * @returns {Boolean}
     */
    getActive(componentItem) {
      const matchingItem = this.items.find((item) => item.component === componentItem)

      return Boolean(matchingItem?.active)
    },

    /**
     * Register an Accordion Item component
     * - Adds entry to the items array.
     * - Calculates if the component should be initially active or not.
     * @param {Object} componentItem - Base Accordion Item component instance
     */
    register(componentItem) {
      const newIndex = this.items.length

      const initialOpen = newIndex === this.defaultIndex

      this.items.push({
        component: componentItem,
        name: componentItem.name,
        active: initialOpen,
      })
    },

    /**
     * Unregister an Accordion Item component
     * - Triggered before the component is unmounted.
     * - Removes component entry from the items array.
     * @param {Object} componentItem - Base Accordion Item component instance
     */
    unregister(componentItem) {
      const index = this.items.findIndex((item) => item.component === componentItem)
      this.items.splice(index, 1)
    },

    /**
     * Set an accordion item's active state.
     * - Receives and accordion item and a new active state
     * - Loops through and updates all accordion component instances.
     *   - Toggles the provided component item open/closed
     *   - If provided accordion item is opening, close the others, unless the
     *     `multiple` prop is true.
     * @param {Object} target - Base Accordion Item to update
     * @param {Object} target.component - Base Accordion Item component instance
     * @param {Object} target.name - Accordion item name
     * @param {Boolean} isActive - new state for accordion item
     */
    setActive(componentItem, isActive) {
      this.items.forEach((item) => {
        const isCurrentComponent = item.component === componentItem
        if (isCurrentComponent) {

          /**
           * If new component state is false/closed, the item is only closed if
           * the `collapsible` prop is true or if any other accordions are open.
           */
          if (!isActive && !this.collapsible && !this.isMultipleActive) {
            return
          }

          item.active = isActive
          return
        }

        if (isActive && !this.multiple) {
          item.active = false
        }
      })
    },

    /**
     * Set an accordion state by it's name.
     * - Checks that the name exists in the items array.
     * - If so toggles
     * @param {String} name - Name of the accordion item to toggle.
     * @param {Boolean} isActive - new state for accordion item.
     */
    setActiveByName(name, isActive) {
      const itemWithName = this.items.find((item) => {
        return item.name.toLowerCase() === name.toLowerCase()
      })

      if (!itemWithName) {
        return
      }

      this.setActive(itemWithName.component, isActive)
    },

    /**
     * Close all accordions.
     */
    closeAll() {
      this.items.forEach((item) => {
        item.active = false
      })
    },
  },
}
</script>

<style lang="scss">
@import './base-accordion';
</style>
